import store from './store/store'

import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { Provider } from 'react-redux'
import { RootPage, AuthenticatedApp, AuthorizationRoute } from './guarding'
import HoneybadgerProvider from './honeybadger'

import CommunityMapPage from './components/community-map/CommunityMapPage'
import CommunityLandingPageWrapper from './components/community-landing-page/CommunityLandingPageWrapper'
import CommunityLandingPage from './components/community-landing-page/CommunityLandingPage'
import EmbeddableWrapper from './components/community-landing-page/EmbeddableWrapper'
import AppWrapper from './components/AppWrapper'
import LogInPage from './components/auth/LogIn/LogInPage'
import OAuthRedirectPage from './components/auth/OAuth/OAuthRedirectPage'
import PostLogoutPage from './components/auth/PostLogout/PostLogoutPage'
import JoinCommunityPage from './components/auth/JoinCommunity/JoinCommunityPage'
import DashboardPage from './components/dashboard/DashboardPage'
import ProShopPage from './components/proShopDisplay'
import ChallengesPage from './components/challenges/ChallengesPage'
import AdminUsersPage from './components/users/Admin/AdminUsersPage'
import ProfilePage from './components/profile/ProfilePage-web'
import AccountPage from './components/account/AccountPage'
import CoachesPage from './components/coaches/CoachesPage'
import ObjectivesPage from './components/objectives/ObjectivesPage'
import CurriculumsPage from './components/curriculums/CurriculumsPage'
import RanksPage from './components/ranks/RanksPage'
import ObjectiveDetailsPage from './components/ranks/ObjectiveDetailsPage'
import LeadsPage from './components/leads/LeadsPage'
import AnnouncementsPage from './components/announcements/AnnouncementsPage'
import CampaignPage from './components/announcements/CampaignPage'
import CommunitiesPage from './components/communities/CommunitiesPage'
import CommunityDetailPage from './components/communities/CommunityDetailPage'
import ReportsPage from './components/reports/ReportsPage'
import ResourceDetailPage from './components/resources/Detail/ResourceDetailPage'
import ResourceSectionsPage from './components/resources/ResourceSections/ResourceSectionsPage'
import ResourceSectionDetailPage from './components/resources/ResourceSections/ResourceSectionDetailPage'
import ProgramsPageWrapper from './components/classes/GroupPrograms/ProgramsPageWrapper'
import ProgramsPage from './components/classes/GroupPrograms/ProgramsPage'
import CoachAcademyPage from './components/education/CoachAcademyPage'
import LessonPlanPage from './components/lessonPlans/LessonPlanPage'
import LessonPlanViewerPage from './components/lessonPlans/viewer/LessonPlanViewerPage'
import LessonPlanBuilderPage from './components/lessonPlans/builder/LessonPlanBuilderPage'
import ConnectStripeRefreshPage from './components/community-settings/PricingTab/ConnectStripeRefreshPage'
import DrillsPage from './components/drills/DrillsPage'
import DrillsBuilder from './components/drills/builder/DrillsBuilder'
import Events from './components/events/Events'
import EventDetail from './components/events/Detail/EventDetail'
import ManagePairings from './components/events/Form/ManagePairings'
import CommunitySettingsPage from './components/community-settings/CommunitySettingsPage'
import PricingTab from './components/community-settings/PricingTab/PricingTab'
import CommunityLandingPageTab from './components/community-settings/CommunityLandingPageTab/CommunityLandingPageTab'
import RegistrationAccessCode from './components/community-settings/RegistrationAccessCodeTab/RegistrationAccessCode'
import GolfClubs from './components/community-settings/GolfClubsTab/GolfClubs'
import ProgramBuilder from './components/classes/ProgramBuilder/ProgramBuilder'
import FourOhFour from './components/common/FourOhFour'
import ErrorBoundary from './components/ErrorBoundary'
import ProgramLandingPage from './components/classes/ProgramLanding/ProgramLandingPage'
import ProgramPresentationPage from './components/classes/ProgramLanding/ProgramPresentationPage/ProgramPresentationPage'
import ProgramRegistrationPage from './components/classes/ProgramRegistration/ProgramRegistrationPage'
import ProgramRegistrationCompletedPage from './components/classes/ProgramRegistration/RegistrationCompletedPage/ProgramRegistrationCompletedPage'
import FamiliesStudentsPage from './components/families/FamiliesStudentsPage'
import MapDetailPageDeprecated from './components/community-map/MapDetailPageDeprecated'
import EventSeries from './components/events/SeriesFlow/EventSeries'
import ProgramDashboardManager from './components/classes/program-dashboard-manager/ProgramDashboardManager'
import OverviewTab from './components/classes/program-dashboard-manager/overview-tab/OverviewTab'
import ScheduleTab from './components/classes/program-dashboard-manager/ScheduleTab'
import LessonPlanTab from './components/classes/program-dashboard-manager/LessonPlanTab'
import SeriesScheduleTab from './components/classes/program-dashboard-manager/SeriesScheduleTab'
import ReportsTab from './components/classes/program-dashboard-manager/ReportsTab'
import RosterTab from './components/classes/program-dashboard-manager/enrollments-tab/RosterTab'
import InviteFamiliesPage from './components/families/InviteFamiliesPage'
import MembersLayout from './components/members/MembersLayout'
import StudentsPage from './components/members/StudentsPage'
import FamiliesPage from './components/members/FamiliesPage'
import InvitedPage from './components/members/InvitedPage'
import MembersListLayout from './components/members/MembersListLayout'
import FamilyLayout from './components/members/FamilyLayout'
import FamilyEnrollmentsPage from './components/members/FamilyEnrollmentsPage'
import FamilyContactPage from './components/members/FamilyContactPage'
import EnrollmentsRedirect from './components/members/EnrollmentsRedirect'


const RegisterRedirect = () => {
  const { pathname } = useLocation()

  const newPath = pathname.replace(
    /^\/register\/(\d+)?/,
    (_, c) => `/join-community/${c}`
  )
  return <Navigate to={newPath === pathname ? '/' : newPath} replace />
}

const App = () => (
  <Provider store={store}>
    <HoneybadgerProvider>
      <BrowserRouter>
        <Routes>
          <Route path='/' element={<RootPage/>} />

          <Route path='/login/legacy' element={<LogInPage />} />
          <Route path='/log-in' element={<Navigate to='/login/legacy' replace />} />
          <Route path='/login/oauth' element={<OAuthRedirectPage />} />
          <Route path='/app/login/oauth' element={<Navigate to={`/login/oauth${window.location.search}`} state={{ mobile: true }} replace />} />
          <Route path='/post-logout' element={<PostLogoutPage />} />
          <Route path='/join-community/:id' element={<JoinCommunityPage />} />

          <Route path='/register/*' element={<RegisterRedirect />} />

          {/* Community Map */}
          <Route path='/community-map' element={<CommunityMapPage />} />
          <Route path='/community-map/:communityId' element={<MapDetailPageDeprecated />} />

          {/* Community Landing Page */}
          <Route element={<EmbeddableWrapper />}>
            <Route path='/landing/:communityId' element={<CommunityLandingPageWrapper />} >
              <Route index element={<CommunityLandingPage />} />

              <Route path='class/:courseId' element={<ProgramLandingPage />}>
                <Route index element={<ProgramPresentationPage />} />
                <Route path='register' element={<ProgramRegistrationPage />} />
                <Route path='registration/:registrationId' element={<ProgramRegistrationCompletedPage />} />
              </Route>
            </Route>
          </Route>

          {/* Main App */}
          <Route element={<AuthenticatedApp><ErrorBoundary><AppWrapper /></ErrorBoundary></AuthenticatedApp>}>
            <Route path='/dashboard/*' element={<DashboardPage />} />
            <Route path='/app/events/:eventId' element={<DashboardPage />} />
            <Route path='/pro-shop' element={<ProShopPage />} />

            <Route path='/profile/:userId' element={<ProfilePage />} />
            <Route path='/app/profile/:userId' element={<ProfilePage />} />
            <Route path='/account' element={<AccountPage />} />

            <Route element={<AuthorizationRoute roles={['Admin', 'Coach', 'FamilyAdmin']} />}>
              <Route path='/coach-academy' element={<CoachAcademyPage />} />
            </Route>

            <Route path='/members' element={<MembersLayout />}>
              <Route path='families/:familyId' element={<FamilyLayout />}>
                <Route index element={<EnrollmentsRedirect />} />
                <Route path='enrollments' element={<FamilyEnrollmentsPage />} />
                <Route path='contact' element={<FamilyContactPage />} />
              </Route>
            </Route>

            <Route element={<AuthorizationRoute roles={['Admin', 'Coach']} />}>
              <Route path='/members' element={<MembersLayout />}>
                <Route element={<MembersListLayout />}>
                  <Route index element={<Navigate to='students' replace />} />
                  <Route path='students' element={<StudentsPage />} />
                  <Route path='families' element={<FamiliesPage />} />
                  <Route path='invited' element={<InvitedPage />} />
                </Route>
              </Route>
              {/* TODO: Move under members/ */}
              <Route path='/students-families' element={<FamiliesStudentsPage />} >
                <Route path='invite-families' element={<InviteFamiliesPage />} />
              </Route>
              <Route path='/leads' element={<LeadsPage />} />
              <Route path='/events' element={<Events/>} />
              <Route path='/events/:eventId' element={<EventDetail />} />
              <Route path='/events/:eventId/manage-pairings' element={<ManagePairings />} />

              <Route path='/objectives' element={<ObjectivesPage />} />
              <Route path='/goals' element={<CurriculumsPage />} />
              <Route path='/ranks' element={<RanksPage />} />
              <Route path='/ranks/:rankId/objective/:objectiveId' element={<ObjectiveDetailsPage />} />
              <Route path='/announcements' element={<AnnouncementsPage />} />
              <Route path='/announcements/new-campaign' element={<CampaignPage />} />
              <Route path='/reports' element={<ReportsPage />} />
              <Route path='/resources/:resourceId' element={<ResourceDetailPage />} />
              <Route path='/resource-sections' element={<ResourceSectionsPage />} />
              <Route path='/resource-sections/:resourceSectionId' element={<ResourceSectionDetailPage />} />
              <Route path='/lesson-plan' element={<LessonPlanPage />} />
              <Route path='/lesson-plan/builder' element={<LessonPlanBuilderPage />} />
              <Route path='/lesson-plan/builder/:lessonPlanId/:isEdit' element={<LessonPlanBuilderPage />} />
              <Route path='/lesson-plan/view/:lessonPlanId' element={<LessonPlanViewerPage />} />

              <Route element={<AuthorizationRoute roles={['Coach']}/>}>
                <Route path='/programs' element={<ProgramsPageWrapper />}>
                  <Route index element={<ProgramsPage />} />
                  <Route path='/programs/:id' element={<ProgramDashboardManager />}>
                    <Route path='overview' element={<OverviewTab />} />
                    <Route path='schedule' element={<ScheduleTab />} />
                    <Route path='lesson-plan' element={<LessonPlanTab />} />
                    <Route path='events' element={<SeriesScheduleTab />} />
                    <Route path='reports' element={<ReportsTab />} />
                    <Route path='roster' element={<RosterTab />} />
                    <Route path='*' element={<Navigate to='/' replace />} />
                  </Route>

                  <Route path='/programs/new' element={<ProgramBuilder />} />
                  <Route path='/programs/:id/edit' element={<ProgramBuilder />} />
                </Route>

                <Route path='/series/new' element={<EventSeries />} />
                <Route path='/series/:id/edit' element={<EventSeries />} />
              </Route>
            </Route>

            <Route element={<AuthorizationRoute roles={['Admin', 'Coach']} />}>
              <Route path='/drills' element={<DrillsPage />} />
              <Route path='/drills/builder' element={<DrillsBuilder />} />
              <Route path='/drills/builder/:drillId' element={<DrillsBuilder />} />
            </Route>

            <Route element={<AuthorizationRoute roles={'Coach'} leadCoachRequired />}>
              <Route path='/community-settings' element={<CommunitySettingsPage />}>
                <Route index element={<Navigate to='pricing' replace />} />
                <Route path='pricing' element={<PricingTab />} />
                <Route path='landing' element={<CommunityLandingPageTab />} />
                <Route path='access-code' element={<RegistrationAccessCode />} />
                <Route path='clubs' element={<GolfClubs />} />
              </Route>
            </Route>

            <Route element={<AuthorizationRoute roles={'Admin'} />}>
              <Route path='/challenges' element={<ChallengesPage />} />
              <Route path='/users' element={<AdminUsersPage />} />
              <Route path='/coaches' element={<CoachesPage />} />
              <Route path='/communities' element={<CommunitiesPage />} />
              <Route path='/communities/:communityId' element={<CommunityDetailPage />} />
            </Route>
          </Route>

          <Route element={<AuthorizationRoute roles={'Coach'} leadCoachRequired />}>
            <Route path='/connect-stripe-refresh' element={<ConnectStripeRefreshPage />} />
          </Route>

          {/* Not found */}
          <Route path='*' element={<FourOhFour />} />
        </Routes>
      </BrowserRouter>
    </HoneybadgerProvider>
  </Provider>
)

export default App
